import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";
const labelStyle = { display: 'block', fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--a1)', marginBottom: 6 };
const inputStyle = { width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '11px 14px', fontSize: 15, color: 'var(--ink)', fontFamily: 'inherit' };

function Shell({ children }) {
  return (
    <PublicLayout theme="archive" grid="--a5" gridStrength={28}>
      <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a1) 0 5px, transparent 6px)' }} />
      <div className="hydlib-pad" style={{ position: 'relative', maxWidth: 520, margin: '0 auto', padding: '72px 48px 96px', boxSizing: 'border-box' }}>
        {children}
      </div>
    </PublicLayout>
  );
}

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setError('No reset token provided. Please request a new password reset link.');
        return;
      }
      try {
        await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired reset link. Please request a new one.');
      } finally {
        setVerifying(false);
      }
    };
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters long'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Shell>
        <p style={{ textAlign: 'center', fontStyle: 'italic', opacity: 0.75, fontSize: 16 }}>Verifying reset link…</p>
      </Shell>
    );
  }

  if (success) {
    return (
      <Shell>
        <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a3)', background: 'var(--paper)', padding: '34px 32px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 30, margin: '0 0 10px' }}>Password reset!</h2>
          <p style={{ fontSize: 15.5, margin: '0 0 22px', opacity: 0.85 }}>Your password has been changed. You can now log in with your new password.</p>
          <Link to="/login" data-testid="go-to-login-button" style={{ display: 'inline-block', border: '1px solid var(--line)', background: 'var(--ink)', color: 'var(--paper)', fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 22px' }}>
            Go to login →
          </Link>
        </div>
      </Shell>
    );
  }

  if (!tokenValid) {
    return (
      <Shell>
        <div style={{ border: '1px solid var(--line)', borderTop: '6px solid var(--a4)', background: 'var(--paper)', padding: '34px 32px', textAlign: 'center' }}>
          <h2 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 30, margin: '0 0 10px' }}>Invalid reset link</h2>
          <p style={{ fontSize: 15, margin: '0 0 22px', opacity: 0.85 }}>{error}</p>
          <Link to="/login" style={{ display: 'inline-block', border: '1px solid var(--line)', background: 'var(--ink)', color: 'var(--paper)', fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '10px 22px' }}>
            Back to login
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 40, margin: '0 0 8px' }}>
        <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>New password</span>
      </h1>
      <p style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 30px' }}>Pick something you&apos;ll remember</p>
      <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: '6px solid var(--a1)', background: 'var(--paper)', padding: '30px 30px' }}>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>New password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} style={inputStyle} placeholder="At least 6 characters" required minLength={6} data-testid="new-password-input" />
          </div>
          <div>
            <label style={labelStyle}>Confirm password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={inputStyle} placeholder="Same again" required minLength={6} data-testid="confirm-password-input" />
          </div>
          {error && <div style={{ border: '1px dashed var(--a4)', background: 'color-mix(in oklch, var(--a4) 8%, var(--paper))', padding: '10px 14px', fontSize: 14, color: 'var(--a4)' }} data-testid="reset-error-message">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="hydlib-highlight"
            data-testid="reset-password-button"
            style={{ cursor: loading ? 'wait' : 'pointer', border: 'none', fontFamily: mono, fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: 13, background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
        <div style={{ marginTop: 18, textAlign: 'center' }}>
          <Link to="/login" style={{ color: 'var(--a3)', fontSize: 14.5, textDecoration: 'underline' }}>Back to login</Link>
        </div>
      </div>
    </Shell>
  );
}

export default ResetPassword;
